import { useState } from 'react';
import type { ReactNode } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * Whether another attempt could plausibly succeed.
 *
 * <p>A 4xx is the server saying the request itself is wrong — not permitted, not found, malformed. Sending
 * it again unchanged produces the same answer, so a retry is pure load. The BFF also answers a downstream
 * identity misconfiguration with `502` rather than `503` precisely so it lands here: a missing Keycloak
 * role is permanent until someone fixes it, and a page that retried it turned one page load into six calls
 * against Keycloak (stage, 2026-08-29).
 */
function isRetryable(error: unknown): boolean {
  // `NormalizedApiError.status` is `number | null` — null for a network or CORS failure, which is exactly
  // the transient case retries exist for. Anything non-numeric is treated the same way.
  const status = (error as NormalizedApiError | undefined)?.status;
  if (typeof status !== 'number') {
    return true;
  }
  // Retry only genuine server-side failures. A 4xx means the request itself is the problem, and 502 means
  // an upstream refused us — both give the same answer however many times they are asked.
  return status >= 500 && status !== 502;
}

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => failureCount < 1 && isRetryable(error),
        refetchOnWindowFocus: false,
      },
    },
  });
}

/** Exposed for tests only — the policy is worth pinning, the provider around it is not. */
// eslint-disable-next-line react-refresh/only-export-components
export const __testing = { isRetryable };

/** Provides the TanStack Query client used for server-state management. */
export function QueryProvider({ children }: QueryProviderProps) {
  // One client per provider instance (stable across re-renders).
  const [queryClient] = useState(createQueryClient);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
